<template>
    <div class="wrapper wrapper--no-max-width">
        <header class="row__header">
            <h1>{{ header }}</h1>
        </header>

        <widget-form :title="title"
            class="widget widget--transparent widget--fluid"
        >
            <template v-if="contactPage">
                <label class="label label--half label--bottom-padding label--disabled">
                    {{ $t('profile.contactInfoPage.phone') }}:

                    <input :value="userDetails.mobileNumber"
                        type="text"
                        disabled
                        class="label__input"
                    >
                </label>
            </template>
            <email :email="userDetails.email"
                :hint="hint"
                @toggleEmail="toggleEmail"
            />
            <div :class="['widget__buttons', {'full-width': contactPage}]">
                <button :id="buttonId"
                    :class="['widget__button', { 'widget__button--disabled': disableButton}]"
                    @click="confirm"
                >
                    {{ buttonText }}
                </button>
            </div>
        </widget-form>
    </div>
</template>

<script>
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Local modules
import localStoreTypes from '@webapp/store/types.js';

// Shared modules
import sharedEpicTypes from '@shared/epics/types.js';

// Components
import email from '@webapp/components/login-process/email.vue';
import widgetForm from '@webapp/components/form/widget-form.vue';

export default {
    name: 'email-verification',
    components: {
        email,
        widgetForm,
    },
    props: {
        buttonId: {
            type: String,
            default: '',
        },
        contactPage: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            required: true,
        },
        hint: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            invalidEmail: false,
            loading: false,
            newEmail: '',
        };
    },
    computed: {
        ...mapGetters([
            'hasSentUserEmailVerification',
            'userDetails',
        ]),

        buttonText() {
            return this.isKycLevel0 ? this.$t('kycEmailVerification.resendEmail') : this.$t('profile.verifyBtn');
        },

        disableButton() {
            return this.invalidEmail || (!this.isKycLevel0 && this.newEmailSameAsOriginal) || this.loading;
        },

        isKycLevel0() {
            return this.userDetails.kycTierLevel === 0;
        },

        newEmailSameAsOriginal() {
            return this.newEmail === this.userDetails.email;
        },
    },
    watch: {
        getLatestSimpleValidationError: 'onValidationError',
        hasSentUserEmailVerification: 'onSentUserEmailVerification',
    },
    mounted() {
        this.newEmail = this.userDetails.email;
    },
    methods: {
        ...mapActions({
            showAppLoader: localStoreTypes.SET_APP_LOADER,
            sendUserEmailVerification: sharedEpicTypes.SEND_USER_EMAIL_VERIFICATION,
        }),

        /**
         * Toggle email verify button and re set email
         *
         * @param {object} payload The payload for email verification
         * @param {boolean} payload.invalidEmail If button is disabled or not
         * @param {string} payload.newEmail The new email
         * @returns {void}
         */
        toggleEmail({
            invalidEmail,
            newEmail,
        }) {
            this.invalidEmail = invalidEmail;
            this.newEmail = newEmail;
        },

        /**
         * On confirm the current form
         *
         * @returns {void}
         */
        async confirm() {
            if (this.disableButton) {
                return;
            }

            this.loading = true;
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            await this.sendUserEmailVerification({
                email: this.newEmail,
            });
        },

        /**
         * Successfully sent user email verification
         *
         * @param {boolean} newValue Check if been sent or not
         * @returns {void}
         */
        onSentUserEmailVerification(newValue) {
            if (newValue) {
                this.$notify({
                    type: 'success',
                    text: this.$t('kycStatusVerification.receiveEmail'),
                });
            }

            this.loading = false;
            this.showAppLoader({
                isVisible: false,
            });
        },

        /**
         * On api validation error
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onValidationError(error) {
            const fetchErrors = this.utilGlobalHandleErrorMessages(error);

            this.$notify({
                type: 'error',
                text: fetchErrors.message,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.widget {
    border: none;

    &__buttons {
        display: flex;
        justify-content: flex-end;

        &.full-width {
            width: 100%;
        }
    }

    &__button {
        @include min-width(md) {
            width: 21.9rem;
        }
    }
}
</style>